// SkillRegistryPage — end-user Skill Registry route shell.
// The public registry is not yet available. Agent-owner skill lifecycle stays
// available separately under the per-agent settings tab.

import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { SUPPORT_SLACK_URL } from '@/shared/helpLinks';

export function SkillRegistryPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const goBack = () => {
    const state = location.state as { from?: unknown } | null;
    navigate(typeof state?.from === 'string' ? state.from : '/', { replace: true });
  };

  return (
    <main className="relative isolate flex min-h-full overflow-hidden bg-muted/20 px-6 py-10 sm:px-10">
      <div className="absolute -left-24 top-12 -z-10 size-72 rounded-full bg-primary/10 blur-3xl" />
      <div className="absolute -right-20 bottom-0 -z-10 size-80 rounded-full bg-primary/5 blur-3xl" />
      <section aria-labelledby="skill-registry-heading" className="mx-auto flex w-full max-w-3xl flex-col items-start gap-8 py-12">
        <Button variant="ghost" className="-ml-3" onClick={goBack}>
          <ArrowLeft />
          Back to Agent Hub
        </Button>
        <div className="rounded-full border border-primary/20 bg-background/80 px-3 py-1 text-xs font-medium text-foreground shadow-sm">
          Coming soon
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex size-14 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary">
            <Sparkles className="size-7" />
          </div>
          <h1 id="skill-registry-heading" className="max-w-2xl text-xl font-semibold tracking-tight">
            Skill Registry is building the expertise your agents need next.
          </h1>
          <p className="max-w-xl text-base leading-7 text-muted-foreground">
            Discover trusted skills, understand what they unlock, and bring the right capability into each agent workflow.
          </p>
        </div>
        <div className="grid w-full gap-3 sm:grid-cols-3">
          {['Discover capabilities', 'Review ownership', 'Adopt with confidence'].map((feature, index) => (
            <div key={feature} className="rounded-xl border border-border/70 bg-background/70 p-4 shadow-sm">
              <span className="text-xs font-medium text-muted-foreground">0{index + 1}</span>
              <p className="mt-5 text-sm font-medium text-foreground">{feature}</p>
            </div>
          ))}
        </div>
        <p className="max-w-xl self-center rounded-xl border border-border/70 bg-background/70 p-4 text-center text-sm leading-6 text-muted-foreground shadow-sm">
          Have an idea for a skill or integration that could make your work easier or improve the Agent Hub for others? Share it in{' '}
          <a
            href={SUPPORT_SLACK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-foreground underline underline-offset-4"
          >
            Slack
          </a>.
          {' '}
          Your idea could shape the next capability.
        </p>
      </section>
    </main>
  );
}
